import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import useGameSounds from "../../hooks/useGameSounds";
import useKidVoice from "../../hooks/useKidVoice";
import Pill from "../../components/Kids/Pill";
import Button from "../../components/Kids/Button";
import OptionTile from "../../components/Kids/OptionTile";
import ProgressBar from "../../components/Kids/ProgressBar";
import EmptyState from "../../components/Kids/EmptyState";

const API = process.env.REACT_APP_API_URL;
const token = () => localStorage.getItem("kids_parent_token");
const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

// README quiz-answer-set tokens (A/B/C/D) — keeps OptionTile's darker-shade map in sync.
const OPT_COLORS = ["#F2506E", "#4F86E0", "#37A65B", "#8B5CF6"];
const LOBBY_BG = "linear-gradient(160deg,#FFF8F2,#FFE9D6,#FFD9BF)";
const ARENA_BG = "#2A3A54"; // README admin navy — Quiz Battle live arena
const MAX_PLAYER_OPTIONS = [2, 3, 4];

// Same route shapes as KidsGameHub's GAME_ROUTES — kept in sync manually
// since only a subset of game types are wired for race mode so far.
const RACE_GAME_ROUTES = {
  puzzle: (slug, id, child) => `/kids/games/puzzle/${slug}?gameId=${id}&childId=${child}`,
};

export default function KidsMultiplayer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const childId = params.get("childId") || sessionStorage.getItem("childId");
  const grade = parseInt(sessionStorage.getItem("childGrade") || "1");

  const sounds = useGameSounds();
  const { speak } = useKidVoice();

  const [phase, setPhase] = useState("menu"); // menu | waiting | countdown | playing | waiting_others | complete
  const [mode, setMode] = useState("quiz"); // quiz | race
  const [code, setCode] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState(null);
  const [countdown, setCountdown] = useState(3);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [maxPlayers, setMaxPlayers] = useState(4);
  const pollRef = useRef(null);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  useEffect(() => {
    // Quiz Battle needs `game_type=quiz` — only quiz games have a `questions`
    // list; `puzzle` games store tile/memory data instead, which used to
    // silently fall back to the same static question bank every time one
    // was picked. Race Battle instead runs the ACTUAL game (currently
    // `puzzle`, e.g. a jigsaw like "Ben-10 Puzzle") independently per
    // player, so it needs the real playable game list, not quiz content.
    // `all_grades=1` — battles are a social, cross-grade activity (siblings
    // challenging each other), so we don't want the strict grade-matching
    // used for solo practice hiding most of the catalog from whichever
    // child didn't happen to create the room.
    const gameType = mode === "race" ? "puzzle" : "quiz";
    setSelectedGame(null);
    axios.get(`${API}/api/kids/games`, { params: { grade, game_type: gameType, all_grades: 1 }, ...auth() })
      .then(r => setGames(r.data?.data || [])).catch(() => {});
  }, [grade, mode]);

  const goBack = () => { clearInterval(pollRef.current); navigate(`/kids/games?childId=${childId}`); };

  const createRoom = async () => {
    if (mode === "race" && !selectedGame) return setError("Choose a game to race on.");
    setLoading(true); setError(null);
    try {
      const body = { child_id: parseInt(childId), max_players: maxPlayers, mode };
      if (selectedGame) body.game_id = selectedGame;
      const r = await axios.post(`${API}/api/kids/multiplayer/create`, body, auth());
      setCode(r.data.data.code);
      setState({ players_count: 1, max_players: maxPlayers, is_host: true, game_name: r.data.data.game_name, status: "waiting", mode });
      setPhase("waiting");
      startPolling(r.data.data.code);
      sounds.playCorrect();
    } catch (e) { setError(e.response?.data?.message || "Failed to create room."); }
    finally { setLoading(false); }
  };

  const joinRoom = async () => {
    if (joinCode.length !== 4) return setError("Enter a 4-character code.");
    setLoading(true); setError(null);
    try {
      const r = await axios.post(`${API}/api/kids/multiplayer/join`, { child_id: parseInt(childId), code: joinCode.toUpperCase() }, auth());
      const roomCode = joinCode.toUpperCase();
      setCode(roomCode);
      setPhase(r.data.data.status === "countdown" ? "countdown" : "waiting");
      startPolling(roomCode);
      sounds.playCorrect();
      if (r.data.data.status === "countdown") speak("Get ready! Game starting!");
    } catch (e) { setError(e.response?.data?.message || "Failed to join room."); }
    finally { setLoading(false); }
  };

  const startNow = async () => {
    setLoading(true); setError(null);
    try {
      await axios.post(`${API}/api/kids/multiplayer/start`, { child_id: parseInt(childId), code }, auth());
    } catch (e) { setError(e.response?.data?.message || "Failed to start."); }
    finally { setLoading(false); }
  };

  const startPolling = useCallback((roomCode) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/api/kids/multiplayer/state`, { params: { code: roomCode, child_id: childId }, ...auth() });
        const s = r.data.data;
        setState(s);

        const p = phaseRef.current;
        if (s.status === "countdown" && p === "waiting") {
          setPhase("countdown");
        }
        if (s.status === "playing" && (p === "countdown" || p === "waiting")) {
          if (s.mode === "race") {
            // Race mode has no in-app play screen of its own — hand off to
            // the actual game (e.g. the jigsaw puzzle screen), which reports
            // back to this room and lands the player on the results screen
            // once they finish, instead of this component's own complete UI.
            clearInterval(pollRef.current);
            const routeFn = RACE_GAME_ROUTES[s.game_type];
            const path = routeFn
              ? `${routeFn(s.game_slug, s.game_id, childId)}&raceCode=${roomCode}`
              : `/kids/multiplayer/race-result?code=${roomCode}&childId=${childId}`;
            navigate(path);
            return;
          }
          setPhase("playing");
          setSelected(null);
          setFeedback(null);
        }
        if (s.status === "complete" && p !== "complete") {
          setPhase("complete");
          clearInterval(pollRef.current);
          const iWon = s.winners?.length === 1 && s.players?.find(pl => pl.is_me)?.name === s.winners[0];
          sounds.playComplete(iWon);
        }
        if (s.status === "playing" && p === "waiting_others" && !s.current_q) {
          setPhase("complete");
          clearInterval(pollRef.current);
        }
      } catch { }
    }, 2000);
  }, [childId, sounds, navigate]);

  useEffect(() => { return () => clearInterval(pollRef.current); }, []);

  useEffect(() => {
    if (phase === "countdown") {
      setCountdown(3);
      const t = setInterval(() => {
        setCountdown(c => {
          if (c <= 1) { clearInterval(t); return 0; }
          return c - 1;
        });
      }, 1000);
      return () => clearInterval(t);
    }
  }, [phase]);

  const submitAnswer = async (ansIdx) => {
    if (selected !== null || !state?.current_q) return;
    setSelected(ansIdx);
    try {
      const r = await axios.post(`${API}/api/kids/multiplayer/answer`, {
        code, child_id: parseInt(childId),
        question_index: state.current_q.index,
        answer_index: ansIdx,
      }, auth());
      const data = r.data.data;
      setFeedback(data.correct ? "correct" : "wrong");
      if (data.correct) { sounds.playCorrect(); } else { sounds.playWrong(); }

      if (data.status === "complete") {
        setTimeout(() => { setPhase("complete"); clearInterval(pollRef.current); sounds.playComplete(true); }, 1200);
        return;
      }

      if (data.my_done) {
        setTimeout(() => { setSelected(null); setFeedback(null); setPhase("waiting_others"); }, 1200);
        return;
      }

      setTimeout(() => { setSelected(null); setFeedback(null); }, 1200);
    } catch { }
  };

  const others = (state?.players || []).filter(p => !p.is_me);
  const me = (state?.players || []).find(p => p.is_me);

  // ── MENU ──
  if (phase === "menu") {
    return (
      <div className="min-h-[100dvh] flex items-start justify-center overflow-y-auto px-4"
        style={{ background: LOBBY_BG, paddingBlockEnd: "max(env(safe-area-inset-bottom, 0px), 16px)", paddingBlockStart: 16 }}>
        <div className="max-w-[400px] w-full text-center pt-10">
          <button onClick={goBack}
            className="fixed top-3 start-3 z-10 bg-white/90 shadow-card font-body font-bold text-[13px] text-muted px-3 py-1.5 rounded-[10px]">
            ← Back
          </button>
          <div className="text-5xl mb-2">⚔️</div>
          <h1 className="font-display font-semibold text-2xl text-ink mb-1.5">{t("kids.multiplayer.title")}</h1>
          <p className="text-sm text-muted mb-4">Challenge friends to a battle!</p>

          {/* Mode toggle */}
          <div className="flex bg-white rounded-card border border-cardline p-1 mb-3 gap-1">
            <button onClick={() => setMode("quiz")}
              className={`flex-1 rounded-[10px] py-2.5 text-xs font-bold ${mode === "quiz" ? "bg-brand text-white" : "text-muted"}`}>
              🧠 Quiz Battle
            </button>
            <button onClick={() => setMode("race")}
              className={`flex-1 rounded-[10px] py-2.5 text-xs font-bold ${mode === "race" ? "bg-brand text-white" : "text-muted"}`}>
              🧩 Race Battle
            </button>
          </div>
          {mode === "race" && (
            <p className="text-xs text-muted mb-3 -mt-1">Everyone plays the same game on their own screen — fastest to finish wins!</p>
          )}

          {/* Game picker */}
          {games.length > 0 ? (
            <div className="bg-white rounded-card border border-cardline p-4 mb-3 text-start">
              <p className="text-xs font-bold text-ink mb-2">{mode === "race" ? "Choose a game to race on:" : "Choose a quiz to battle on:"}</p>
              <div className="flex flex-wrap gap-1.5">
                {mode === "quiz" && (
                  <Pill active={selectedGame === null} onClick={() => setSelectedGame(null)} className="!min-h-0 !py-1.5 !text-xs">
                    🎲 Random
                  </Pill>
                )}
                {games.map(g => (
                  <Pill key={g.id} active={selectedGame === g.id} onClick={() => setSelectedGame(g.id)} className="!min-h-0 !py-1.5 !text-xs">
                    {g.name} <span className="opacity-60">(G{g.min_grade}-{g.max_grade})</span>
                  </Pill>
                ))}
              </div>
            </div>
          ) : mode === "race" && (
            <div className="mb-3">
              <EmptyState emoji="🧩" title="No race-ready games yet" subtitle="Puzzle games will show up here once available." className="!py-4 !gap-1.5" />
            </div>
          )}

          {/* Player count picker */}
          <div className="bg-white rounded-card border border-cardline p-4 mb-3 text-start">
            <p className="text-xs font-bold text-ink mb-2">How many players?</p>
            <div className="flex gap-1.5">
              {MAX_PLAYER_OPTIONS.map(n => (
                <Pill key={n} active={maxPlayers === n} onClick={() => setMaxPlayers(n)} className="!min-h-0 !py-1.5 !text-xs">
                  {n} players
                </Pill>
              ))}
            </div>
          </div>

          <Button onClick={createRoom} disabled={loading || (mode === "race" && !selectedGame)} className="w-full mb-3">
            🎯 {t("kids.multiplayer.createRoom")}
          </Button>

          <div className="bg-white rounded-card border border-cardline shadow-card p-5 text-start">
            <p className="text-[13px] font-bold text-ink mb-2.5">Or join a friend's room:</p>
            <div className="flex gap-2">
              <input value={joinCode} onChange={e => setJoinCode(e.target.value.toUpperCase().slice(0, 4))}
                placeholder="CODE"
                className="flex-1 px-4 py-3 rounded-xl font-mono text-xl font-extrabold text-center tracking-[4px] uppercase outline-none border-2"
                style={{ borderColor: "#E5DED4" }}
                maxLength={4} />
              <button onClick={joinRoom} disabled={loading || joinCode.length < 4}
                className={`px-5 py-3 rounded-xl text-white font-bold text-sm ${joinCode.length === 4 ? "bg-success" : "bg-faint"}`}>
                {t("kids.multiplayer.joinRoom")}
              </button>
            </div>
          </div>

          {error && (
            <div className="mt-3">
              <EmptyState emoji="😕" title={error} className="!py-3 !gap-1.5" />
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── WAITING (lobby: room open, host can add more players or start early) ──
  if (phase === "waiting") {
    const count = state?.players_count ?? 1;
    const max = state?.max_players ?? maxPlayers;
    const canStart = state?.is_host && count >= 2;
    return (
      <div className="min-h-screen flex items-center justify-center p-5" style={{ background: "linear-gradient(160deg,#FFF8F2,#FFE9D6)" }}>
        <div className="max-w-[400px] w-full text-center">
          <div className="text-5xl mb-3 ar-pulse">⏳</div>
          <h2 className="font-display font-semibold text-2xl text-ink mb-2">{t("kids.multiplayer.waiting")}</h2>
          <p className="text-[13px] text-muted mb-5">Share this code with your friends:</p>
          <div className="bg-white border-[3px] border-brand rounded-card inline-block px-6 py-4 mb-4">
            <span className="font-mono text-4xl font-black text-brand tracking-[8px]">{code}</span>
          </div>

          <div className="bg-white rounded-card border border-cardline p-4 mb-4 text-start">
            <p className="text-xs font-bold text-ink mb-2">{count}/{max} players joined</p>
            <div className="flex flex-col gap-1.5">
              {(state?.players || []).map(p => (
                <div key={p.child_id} className="flex items-center gap-2 text-sm text-ink">
                  <span>{p.is_me ? "🧑" : "👤"}</span>
                  <span className="font-semibold">{p.name}{p.is_me ? " (you)" : ""}</span>
                </div>
              ))}
              {Array.from({ length: Math.max(0, max - count) }).map((_, i) => (
                <div key={`empty-${i}`} className="flex items-center gap-2 text-sm text-faint">
                  <span>⏳</span><span>Waiting for player...</span>
                </div>
              ))}
            </div>
          </div>

          {canStart && (
            <Button onClick={startNow} disabled={loading} className="w-full mb-3">
              ▶ Start Now ({count} players)
            </Button>
          )}
          {!state?.is_host && <p className="text-xs text-faint mb-3">Waiting for the host to start the battle…</p>}

          <p className="text-xs text-faint">Room expires in 5 minutes</p>
          <button onClick={goBack} className="mt-5 bg-transparent border-2 border-cardline rounded-xl px-6 py-2.5 font-bold text-muted">
            Cancel
          </button>
          {error && <div className="mt-3"><EmptyState emoji="😕" title={error} className="!py-3 !gap-1.5" /></div>}
          <style>{`.ar-pulse { animation: pulse 2s ease-in-out infinite } @keyframes pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.1)} }`}</style>
        </div>
      </div>
    );
  }

  // ── COUNTDOWN ──
  if (phase === "countdown") {
    const otherNames = others.map(p => p.name).join(", ");
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand to-brand-light">
        <div className="text-center text-white">
          <p className="text-lg font-bold mb-2 opacity-80">
            {otherNames ? `vs ${otherNames}` : "Get Ready!"}
          </p>
          <div className="font-display font-black text-[120px] leading-none" style={{ textShadow: "0 4px 30px rgba(0,0,0,.3)" }}>
            {countdown > 0 ? countdown : "GO!"}
          </div>
          <p className="text-sm opacity-70 mt-2">{state?.game_name}</p>
        </div>
      </div>
    );
  }

  // ── PLAYING ──
  if (phase === "playing" && state?.current_q) {
    const q = state.current_q;
    const roster = [...(state.players || [])].sort((a, b) => b.score - a.score);
    return (
      <div className="min-h-screen flex flex-col" style={{ background: ARENA_BG }}>
        {/* Score roster */}
        <div className="flex items-center gap-2 px-4 py-3 overflow-x-auto">
          {roster.map(p => (
            <div key={p.child_id}
              className={`shrink-0 rounded-xl px-3 py-1.5 text-center ${p.is_me ? "bg-white/20" : "bg-white/10"}`}>
              <div className="text-[10px] font-bold text-white/60 whitespace-nowrap max-w-[80px] overflow-hidden text-ellipsis">
                {p.is_me ? t("kids.multiplayer.you").toUpperCase() : p.name}
              </div>
              <div className="font-display font-black text-base text-white">{p.score}</div>
            </div>
          ))}
        </div>
        <div className="text-center px-4">
          <div className="text-xs font-bold text-white/50">Q{q.index + 1}/{q.total}</div>
          <ProgressBar value={(q.index + 1) / q.total} className="w-[140px] mx-auto mt-1.5 !h-1" />
        </div>

        {/* Question */}
        <div className="flex-1 flex flex-col items-center justify-center px-5 pb-5">
          <div className="bg-white/10 backdrop-blur-sm rounded-card-lg px-7 py-6 max-w-[500px] w-full text-center mb-6">
            <p className="text-white font-display font-semibold text-lg leading-snug m-0">{q.q}</p>
          </div>

          <div className="grid grid-cols-2 gap-3 max-w-[500px] w-full">
            {q.opts.map((opt, i) => {
              let tileState = "idle";
              if (feedback && selected === i) tileState = feedback === "correct" ? "correct" : "wrong";
              else if (feedback && selected !== null && selected !== i) tileState = "dim";
              return (
                <OptionTile key={i} letter={String.fromCharCode(65 + i)} color={OPT_COLORS[i]}
                  state={tileState} onClick={() => submitAnswer(i)}>
                  {opt}
                </OptionTile>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ── WAITING FOR OTHER PLAYERS ──
  if (phase === "waiting_others") {
    const stillPlaying = others.filter(p => p.answered < (state?.total_q || 5));
    return (
      <div className="min-h-screen flex items-center justify-center p-5" style={{ background: ARENA_BG }}>
        <div className="text-center text-white">
          <div className="text-5xl mb-3 ar-pulse">⏳</div>
          <h2 className="font-display font-semibold text-2xl mb-2">You finished!</h2>
          <p className="text-sm opacity-70 mb-1">Your score: <b>{me?.score ?? 0}</b> points</p>
          <p className="text-[13px] opacity-50">
            {stillPlaying.length > 0
              ? `Waiting for ${stillPlaying.map(p => p.name).join(", ")} to finish...`
              : "Waiting for results..."}
          </p>
          <style>{`.ar-pulse { animation: pulse 2s ease-in-out infinite } @keyframes pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.1)} }`}</style>
        </div>
      </div>
    );
  }

  // ── COMPLETE ──
  if (phase === "complete" && state) {
    const roster = [...(state.players || [])].sort((a, b) => b.score - a.score);
    const winners = state.winners || [];
    const iWon = me && winners.includes(me.name) && winners.length === 1;
    const tied = winners.length > 1;
    const totalQ = state.total_q || 5;

    const shareText = `I just ${iWon ? "WON" : tied ? "tied" : "played"} a Quiz Battle on Cabil Kids! ${me?.score ?? 0} points. Challenge me at cabil.co/kids! 🏆`;

    const resultBg = iWon
      ? "linear-gradient(160deg,#F5B31E,#FF8A4C)"
      : tied
      ? "linear-gradient(160deg,#9B7BFF,#6A47E0)"
      : ARENA_BG;

    return (
      <div className="min-h-screen flex items-center justify-center p-5" style={{ background: resultBg }}>
        <style>{`@keyframes confetti { 0%{transform:translateY(-10vh) rotate(0)} 100%{transform:translateY(110vh) rotate(720deg)} }`}</style>
        {iWon && [...Array(18)].map((_, i) => (
          <div key={i} className="fixed top-0 text-2xl pointer-events-none"
            style={{ insetInlineStart: `${Math.random() * 100}%`, animation: `confetti ${2 + Math.random() * 2}s linear ${Math.random() * 2}s infinite` }}>
            {["⭐", "🏆", "🎉", "✨", "🥇"][Math.floor(Math.random() * 5)]}
          </div>
        ))}
        <div className="max-w-[380px] w-full text-center">
          <div className="text-8xl mb-2">{iWon ? "🏆" : tied ? "🤝" : "💪"}</div>
          <h1 className="font-display font-semibold text-[30px] text-white mb-1">
            {iWon ? t("kids.multiplayer.youWin") : tied ? t("kids.multiplayer.tie") : t("kids.multiplayer.youLose")}
          </h1>
          <p className="text-white/60 text-[13px] mb-5">{state.game_name}</p>

          {/* Leaderboard */}
          <div className="flex flex-col gap-2 mb-5">
            {roster.map((p, i) => {
              const isWinner = winners.includes(p.name);
              const correct = p.answered ? Math.round(p.score / 100) : Math.round(p.score / 100);
              return (
                <div key={p.child_id}
                  className={`flex items-center justify-between bg-white/15 rounded-card px-4 py-3 border-2 ${isWinner ? "border-gold" : "border-transparent"} ${p.is_me ? "" : "bg-white/10"}`}>
                  <div className="flex items-center gap-2 text-start">
                    <span className="font-display font-black text-white/50 text-sm w-4">{i + 1}</span>
                    <span className="font-semibold text-white text-sm">
                      {p.is_me ? t("kids.multiplayer.you") : p.name}
                    </span>
                    {isWinner && <span className="text-[9px] font-extrabold text-gold">🏆</span>}
                  </div>
                  <div className="text-end">
                    <div className="font-display font-black text-xl text-white">{p.score}</div>
                    <div className="text-[10px] text-white/50">{correct}/{totalQ} correct</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Action buttons */}
          <Button variant="secondary" onClick={() => { setPhase("menu"); setState(null); setCode(""); setJoinCode(""); setSelectedGame(null); }}
            className="w-full mb-2.5">
            ⚔️ {t("kids.multiplayer.playAgain")}
          </Button>
          <button onClick={() => { if (navigator.share) { navigator.share({ title: "Cabil Kids Quiz Battle", text: shareText, url: "https://cabil.co/kids" }); } else { navigator.clipboard.writeText(shareText); alert("Result copied! Share it with friends."); } }}
            className="w-full py-3 rounded-2xl bg-white/20 text-white font-display font-semibold text-sm mb-2.5">
            📤 Share Result
          </button>
          <button onClick={goBack}
            className="w-full py-2.5 rounded-2xl border-2 border-white/20 bg-transparent text-white/70 font-display font-semibold text-[13px]">
            ← Back to Games
          </button>
        </div>
      </div>
    );
  }

  // ── Loading/fallback ──
  return (
    <div className="min-h-screen flex items-center justify-center text-white" style={{ background: ARENA_BG }}>
      <div className="text-center">
        <div className="text-5xl mb-2" style={{ animation: "pulse 2s infinite" }}>⚔️</div>
        <p className="font-display font-semibold text-base">Loading battle...</p>
        <style>{`@keyframes pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.1)} }`}</style>
      </div>
    </div>
  );
}
